export const MIN_ZOOM_LOCKED = 1;
export const MIN_ZOOM_FREE = 0.25;
export const MAX_ZOOM = 3;
const FREE_ZOOM_VISIBLE_MARGIN = 28;

export const STAGE_MIN_WIDTH = 280;
export const STAGE_MAX_WIDTH = 720;
export const STAGE_MAX_HEIGHT = 560;
export const STAGE_WRAP_PADDING = 38;

/**
 * Safe CSS `url("...")` for background-image. Quotes + escape so spaces in
 * S3 keys (e.g. `Rectangle 66_….png`) do not invalidate the declaration.
 */
export function toCssUrl(imageUrl?: string) {
  if (!imageUrl) return '';
  const escaped = String(imageUrl).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

export function computeStageSize({
  wrapWidth,
  ratio,
}: { wrapWidth?: number; ratio?: number } = {}) {
  const safeRatio = ratio && ratio > 0 ? ratio : 1;
  const availableW = Math.min(
    STAGE_MAX_WIDTH,
    Math.max(STAGE_MIN_WIDTH, (wrapWidth || 0) - STAGE_WRAP_PADDING)
  );
  let width = availableW;
  let height = width / safeRatio;
  if (height > STAGE_MAX_HEIGHT) {
    height = STAGE_MAX_HEIGHT;
    width = height * safeRatio;
  }
  return { width: Math.round(width), height: Math.round(height) };
}

export function computeCoverScale({
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
}: {
  containerWidth?: number;
  containerHeight?: number;
  imageWidth?: number;
  imageHeight?: number;
} = {}) {
  if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) return 1;
  return Math.max(containerWidth / imageWidth, containerHeight / imageHeight);
}

export function centerOffset({
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
  scale,
}: {
  containerWidth: number;
  containerHeight: number;
  imageWidth: number;
  imageHeight: number;
  scale: number;
}) {
  return {
    x: (containerWidth - imageWidth * scale) / 2,
    y: (containerHeight - imageHeight * scale) / 2,
  };
}

export function clampOffset({
  offset,
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
  scale,
  allowFreeZoom,
}: {
  offset: { x: number; y: number };
  containerWidth: number;
  containerHeight: number;
  imageWidth: number;
  imageHeight: number;
  scale: number;
  allowFreeZoom?: boolean;
}) {
  const renderedWidth = imageWidth * scale;
  const renderedHeight = imageHeight * scale;

  if (allowFreeZoom) {
    return {
      x: Math.min(
        containerWidth - FREE_ZOOM_VISIBLE_MARGIN,
        Math.max(FREE_ZOOM_VISIBLE_MARGIN - renderedWidth, offset.x)
      ),
      y: Math.min(
        containerHeight - FREE_ZOOM_VISIBLE_MARGIN,
        Math.max(FREE_ZOOM_VISIBLE_MARGIN - renderedHeight, offset.y)
      ),
    };
  }

  return {
    x: Math.min(0, Math.max(containerWidth - renderedWidth, offset.x)),
    y: Math.min(0, Math.max(containerHeight - renderedHeight, offset.y)),
  };
}

export function zoomAroundPoint({
  containerWidth,
  containerHeight,
  offset,
  oldScale,
  newScale,
  pointX,
  pointY,
}: {
  containerWidth: number;
  containerHeight: number;
  offset: { x: number; y: number };
  oldScale: number;
  newScale: number;
  pointX?: number;
  pointY?: number;
}) {
  const px = pointX ?? containerWidth / 2;
  const py = pointY ?? containerHeight / 2;
  const sourceX = (px - offset.x) / oldScale;
  const sourceY = (py - offset.y) / oldScale;

  return {
    x: px - sourceX * newScale,
    y: py - sourceY * newScale,
  };
}

export function clampZoom(zoom: number, { min, max = MAX_ZOOM }: { min: number; max?: number }) {
  return Math.min(max, Math.max(min, zoom));
}

export function computeDrawParams({
  containerWidth,
  containerHeight,
  offset,
  scale,
  imageWidth,
  imageHeight,
  outputWidth,
  outputHeight,
}: {
  containerWidth: number;
  containerHeight: number;
  offset: { x: number; y: number };
  scale: number;
  imageWidth: number;
  imageHeight: number;
  outputWidth: number;
  outputHeight: number;
}) {
  const outputScaleX = outputWidth / containerWidth;
  const outputScaleY = outputHeight / containerHeight;

  return {
    dx: offset.x * outputScaleX,
    dy: offset.y * outputScaleY,
    dWidth: imageWidth * scale * outputScaleX,
    dHeight: imageHeight * scale * outputScaleY,
  };
}

export function normalizeCropOffset({
  offset,
  containerWidth,
  containerHeight,
}: {
  offset?: { x: number; y: number };
  containerWidth?: number;
  containerHeight?: number;
} = {}) {
  if (!offset || !containerWidth || !containerHeight) return { offsetX: null, offsetY: null };
  return {
    offsetX: offset.x / containerWidth,
    offsetY: offset.y / containerHeight,
  };
}

export function denormalizeCropOffset({
  offsetX,
  offsetY,
  containerWidth,
  containerHeight,
}: {
  offsetX?: number | null;
  offsetY?: number | null;
  containerWidth?: number;
  containerHeight?: number;
} = {}) {
  if (offsetX == null || offsetY == null || !containerWidth || !containerHeight) {
    return null;
  }
  return {
    x: Number(offsetX) * containerWidth,
    y: Number(offsetY) * containerHeight,
  };
}

export function computeCroppedImageLayout({
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
  zoom,
  offsetX,
  offsetY,
  frameRatio,
}: {
  containerWidth?: number;
  containerHeight?: number;
  imageWidth?: number;
  imageHeight?: number;
  zoom?: number | null;
  offsetX?: number | null;
  offsetY?: number | null;
  frameRatio?: number;
} = {}) {
  if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) return null;

  const hasCropFrame = offsetX != null && offsetY != null && frameRatio != null && frameRatio > 0;
  if (hasCropFrame) {
    const virtualWidth = 1200;
    const virtualHeight = virtualWidth / frameRatio;
    const framed = computeCroppedImageLayoutOnFrame({
      containerWidth: virtualWidth,
      containerHeight: virtualHeight,
      imageWidth,
      imageHeight,
      zoom,
      offsetX,
      offsetY,
    });
    if (!framed) return null;

    const frameCover = computeCoverScale({
      containerWidth,
      containerHeight,
      imageWidth: virtualWidth,
      imageHeight: virtualHeight,
    });
    const frameLeft = (containerWidth - virtualWidth * frameCover) / 2;
    const frameTop = (containerHeight - virtualHeight * frameCover) / 2;

    return {
      width: framed.width * frameCover,
      height: framed.height * frameCover,
      left: frameLeft + framed.left * frameCover,
      top: frameTop + framed.top * frameCover,
      scale: framed.scale * frameCover,
    };
  }

  return computeCroppedImageLayoutOnFrame({
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
    zoom,
    offsetX,
    offsetY,
  });
}

function computeCroppedImageLayoutOnFrame({
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
  zoom,
  offsetX,
  offsetY,
}: {
  containerWidth: number;
  containerHeight: number;
  imageWidth: number;
  imageHeight: number;
  zoom?: number | null;
  offsetX?: number | null;
  offsetY?: number | null;
}) {
  const safeZoom = zoom != null && Number(zoom) > 0 ? Number(zoom) : 1;
  const cover = computeCoverScale({ containerWidth, containerHeight, imageWidth, imageHeight });
  const scale = cover * safeZoom;
  const denormalized = denormalizeCropOffset({ offsetX, offsetY, containerWidth, containerHeight });
  const offset =
    denormalized ||
    centerOffset({ containerWidth, containerHeight, imageWidth, imageHeight, scale });

  return {
    width: imageWidth * scale,
    height: imageHeight * scale,
    left: offset.x,
    top: offset.y,
    scale,
  };
}

export function computeCropBackgroundStyle({
  imageUrl,
  containerWidth,
  containerHeight,
  imageWidth,
  imageHeight,
  zoom,
  offsetX,
  offsetY,
  frameRatio,
}: {
  imageUrl?: string;
  containerWidth?: number;
  containerHeight?: number;
  imageWidth?: number;
  imageHeight?: number;
  zoom?: number | null;
  offsetX?: number | null;
  offsetY?: number | null;
  frameRatio?: number;
} = {}) {
  if (!imageUrl) return undefined;
  const layout = computeCroppedImageLayout({
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
    zoom,
    offsetX,
    offsetY,
    frameRatio,
  });
  if (!layout) {
    return {
      backgroundImage: toCssUrl(imageUrl),
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  return {
    backgroundImage: toCssUrl(imageUrl),
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${layout.width}px ${layout.height}px`,
    backgroundPosition: `${layout.left}px ${layout.top}px`,
  };
}

export function shouldShowCropBlurFill({
  zoomLibre,
  zoom,
}: { zoomLibre?: boolean; zoom?: number | null } = {}) {
  if (!zoomLibre) return false;
  const safeZoom = zoom != null && Number(zoom) > 0 ? Number(zoom) : 1;
  return safeZoom < 1;
}
